import { useEffect, useState } from 'react';

/**
 * useState that survives reloads via localStorage.
 * Every access is guarded — a browser with storage disabled degrades to
 * in-memory state rather than throwing on render.
 */
export function usePersistedState<T>(
    key: string,
    initial: T
): [T, React.Dispatch<React.SetStateAction<T>>] {
    const [value, setValue] = useState<T>(() => {
        try {
            const raw = window.localStorage.getItem(key);
            return raw === null ? initial : (JSON.parse(raw) as T);
        } catch {
            return initial;
        }
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // Storage full or blocked — keep working in memory.
        }
    }, [key, value]);

    return [value, setValue];
}

export default usePersistedState;
